import { ArrowUpRight, BatteryCharging, Linkedin } from "lucide-react";
import { useLanguage } from "@/i18n";
import { Reveal, Chapter } from "./shared";
import { Insights } from "./Insights";

const ABOUT_IMG = "/assets/tiger-forest.jpg";
const OFFICE_IMG = "/assets/electro-office.jpg";

export const About = () => {
  const { t } = useLanguage();
  return (
    <section id="about" className="scroll-mt-24 bg-white" data-testid="about-section">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-10 lg:py-32">
        <Chapter num="01" label={t.about.chapter.split("/ ")[1]} />
        <div className="mt-12 grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <h2 data-testid="about-heading" className="font-display text-3xl font-extrabold leading-[1.08] tracking-tight text-neutral-950 sm:text-4xl lg:text-5xl">
                {t.about.heading}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p data-testid="about-body" className="mt-7 max-w-2xl text-base leading-relaxed text-neutral-500 sm:text-lg">
                {t.about.body}
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-8" data-testid="about-group-links">
                <div className="mb-3 font-mono text-[11px] tracking-[0.25em] text-brand-700">{t.about.groupLabel}</div>
                <div className="flex flex-wrap gap-3">
                  {[
                    ["arthurtoren.co.uk", "https://arthurtoren.co.uk", "about-link-arthurtoren"],
                    ["path.com.pl", "https://path.com.pl", "about-link-path"],
                    ["hadronenergy.pl", "https://hadronenergy.pl", "about-link-hadron"],
                    ["margtor.africa", "https://margtor.africa", "about-link-margtor"],
                  ].map(([label, href, id]) => (
                    <a
                      key={href}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={id}
                      className="group inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-paper px-4 py-2 font-mono text-xs font-medium text-neutral-600 transition-[border-color,color,background-color] duration-300 hover:border-brand-600 hover:bg-brand-100 hover:text-brand-700"
                    >
                      {label}
                      <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </a>
                  ))}
                </div>
                <div data-testid="about-hadron-note" className="mt-5 flex items-start gap-4 rounded-2xl border border-brand-200 bg-brand-50 p-5">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-600 text-white">
                    <BatteryCharging size={19} />
                  </span>
                  <p className="text-sm leading-relaxed text-neutral-600">
                    {t.about.hadron}{" "}
                    <a
                      href="https://hadronenergy.pl"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="about-hadron-link"
                      className="font-semibold text-brand-700 underline decoration-brand-200 underline-offset-4 transition-colors hover:text-brand-500"
                    >
                      hadronenergy.pl
                    </a>
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
          <div className="relative lg:col-span-5">
            <Reveal delay={0.12} className="relative">
              <div className="overflow-hidden rounded-[1.75rem]" data-testid="about-image-main">
                <img src={ABOUT_IMG} alt="Amperi electric scooter" className="aspect-square w-full object-cover transition-transform duration-700 hover:scale-105" />
              </div>
              <div className="absolute -bottom-8 -left-4 w-40 overflow-hidden rounded-2xl border-4 border-white shadow-xl sm:-left-10 sm:w-52" data-testid="about-image-secondary">
                <img src={OFFICE_IMG} alt="electro.technology HQ in Kraków" className="aspect-[4/3] w-full object-cover" />
              </div>
            </Reveal>
          </div>
        </div>

        <div className="mt-24 grid border-t border-neutral-200 md:grid-cols-3">
          {t.about.tenets.map(([title, desc], i) => (
            <Reveal key={i} delay={i * 0.1} className={`px-2 py-10 sm:px-8 ${i > 0 ? "md:border-l md:border-neutral-200" : ""} max-md:border-t max-md:border-neutral-100`}>
              <div className="font-mono text-xs tracking-[0.25em] text-brand-600" data-testid={`tenet-number-${i}`}>{`0${i + 1}`}</div>
              <h3 className="mt-3 font-display text-xl font-bold text-neutral-900" data-testid={`tenet-title-${i}`}>{title}</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-neutral-500">{desc}</p>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.08}>
          <div data-testid="expert-card" className="mt-20 flex flex-col gap-8 rounded-[1.75rem] border border-neutral-200 bg-gradient-to-br from-brand-50 to-white p-7 sm:flex-row sm:items-center sm:p-9">
            <div className="shrink-0 self-start overflow-hidden rounded-2xl sm:w-44">
              <img
                src="/assets/gregory-radek.jpg"
                alt="Gregory Radek — CEO, electro.technology"
                loading="lazy"
                className="aspect-[3/4] w-full object-cover transition-transform duration-700 hover:scale-105"
                data-testid="expert-photo"
              />
            </div>
            <div className="min-w-0">
              <div className="font-mono text-[10px] tracking-[0.25em] text-brand-700">{t.about.expertLabel}</div>
              <h3 className="mt-2 font-display text-2xl font-extrabold tracking-tight text-neutral-950 sm:text-3xl" data-testid="expert-name">
                {t.about.expertName} <span className="text-base font-bold text-neutral-400">· {t.about.expertRole}</span>
              </h3>
              <p className="mt-2.5 font-display text-lg font-bold leading-snug text-brand-700 sm:text-xl" data-testid="expert-title">
                {t.about.expertTitle}
              </p>
              <p className="mt-2.5 max-w-2xl text-sm leading-relaxed text-neutral-500">{t.about.expertBio}</p>
              <a
                href="https://www.linkedin.com/in/gregradek/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="expert-linkedin-link"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#0A66C2] px-5 py-2.5 text-xs font-semibold text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#0958a8]"
              >
                <Linkedin size={14} /> {t.about.expertCta}
              </a>
            </div>
          </div>
        </Reveal>

        <Insights />

        <Reveal delay={0.1}>
          <div className="mt-20" data-testid="about-youtube-embed">
            <div className="mb-5 flex items-center gap-3">
              <span className="grid h-8 w-8 place-items-center rounded-full bg-red-600 text-white">
                <ArrowUpRight size={14} />
              </span>
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">{t.about.watch}</span>
            </div>
            <div className="overflow-hidden rounded-[1.75rem] border border-neutral-200 shadow-[0_30px_70px_-40px_rgba(44,56,28,0.4)]">
              <iframe
                src="https://www.youtube.com/embed/videoseries?list=PLjG2JQx9YDFYBsxQQuPxayvvboXaNL2AI"
                title="Julia Amperi — mini series"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="aspect-video w-full"
              />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
